<template>
    <v-app>
        <hrm-letter-setup :dialog="letterInfoDialog" :data="data" @update:dialog="letterInfoDialog = $event" />  

        <v-container fluid>
            <v-row>
                <v-col>
                    <v-card>
                        <v-card-title class="panel-header"> 
                            Letter Info 
                             <v-btn
                                            variant="outlined"
                                            color="primary"
                                        class="text-none"
                                            bg-color="blue-lighten-5"
                                            append-icon="mdi-information-outline"
                                            @click="showKeywordsModal = true"
                                            >
                                                Keywords
                                            </v-btn>

                        </v-card-title>
                        <v-card-text>
                            <v-form ref="addLetter" id="addLetter">
                                <v-row class="pt-2">
                                    <v-col cols="12" class="px-8">
                                        <v-label class="text-subtitle-1 font-weight-bold mb-1">
                                            Letter Type <span class="text-red-accent-4">*</span>
                                        </v-label>
                                          

                                        <v-col cols="12" md="3" class="pa-0">
                                            <v-autocomplete 
                                            v-model="selectedLetterId"
                                            class="visually-hidden-label"
                                            label="Letter Type" 
                                            variant="outlined" 
                                            density="compact"
                                            bg-color="grey-lighten-4" 
                                            :items="getHrmLetterInfo"
                                            :item-title="(item) => `${item.name}`"
                                            item-value="id" 
                                            :error-messages="id.errorMessage.value"
                                            autocomplete="off" 
                                            clearable>
                                                <template v-slot:append-inner>
                                                    <v-slide-x-reverse-transition mode="out-in">
                                                        <v-icon
                                                        @mousedown.stop="openAddLetterInfo"
                                                        icon="mdi-plus-circle-outline"
                                                        color="primary"
                                                        size="24"
                                                        class="cursor-pointer"
                                                        ></v-icon>
                                                    </v-slide-x-reverse-transition>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        
                                        
                                        <div class="d-flex justify-space-between align-center mb-2">
                                            <v-label class="text-subtitle-1 font-weight-bold">
                                                To<span class="text-red-accent-4">*</span>
                                            </v-label>
                                        </div>

                                        <Ckeditor
                                            :editor="ClassicEditor"
                                            v-model="to.value.value"
                                            :config="editorConfig"
                                        />
                                        

                                        <v-label class="text-subtitle-1 font-weight-bold mt-4 mb-1">
                                            Subject<span class="text-red-accent-4">*</span>
                                        </v-label>

                                        <v-text-field
                                        label="Subject"
                                        class="visually-hidden-label"
                                        density="compact"
                                        single-line
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        v-model="subject.value.value"
                                        :error-messages="subject.errorMessage.value"
                                        autocomplete="off"
                                        clearable
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" class="px-8 pt-0">
                                        <div class="d-flex justify-space-between align-center mb-2">
                                            <v-label class="text-subtitle-1 font-weight-bold">
                                                Letter Body<span class="text-red-accent-4">*</span>
                                            </v-label>
                                        </div>

                                        <Ckeditor
                                            :editor="ClassicEditor"
                                            v-model="body.value.value"
                                            :config="editorConfig"
                                        />
                                    </v-col>

                                    <v-col cols="12" class="px-8">
                                        <v-label class="text-subtitle-1 font-weight-bold mb-1">
                                            Letter Terms & Conditions
                                        </v-label>

                                        <Ckeditor
                                        :editor="ClassicEditor"
                                        v-model="terms.value.value"
                                        :config="editorConfig"
                                        />
                                    </v-col>

                                    <v-col cols="12" class="px-8">
                                        <v-label class="text-subtitle-1 font-weight-bold mb-1">
                                            Letter Regards<span class="text-red-accent-4">*</span>
                                        </v-label>

                                        <Ckeditor
                                        :editor="ClassicEditor"
                                        v-model="regards.value.value"
                                        :config="editorConfig"
                                        />
                                    </v-col>

                                    <v-col>
                                        <v-layout class="justify-end px-8 pb-4">
                                            <v-btn
                                            class="bg-primary"
                                            variant="flat"
                                            @click="submitForm"
                                            :loading="loading">
                                                UPDATE
                                            </v-btn>
                                        </v-layout>
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- Keywords Modal -->
        <v-dialog v-model="showKeywordsModal" max-width="900" scrollable>
            <v-card>
                <v-card-title>
                    <v-icon>mdi-file-document-outline</v-icon>
                    Available Keywords for Letters
                </v-card-title>

                <v-card-subtitle class="py-4 px-6">
                    Use these keywords in your letter content to automatically populate employee information. Simply copy the keyword and paste it where needed.
                </v-card-subtitle>

                <v-divider></v-divider>

                <v-card-text class="pa-6">
                    <!-- Search Bar -->
                    <v-text-field
                        v-model="keywordSearch"
                        label="Search keywords..."
                        variant="outlined"
                        density="compact"
                        prepend-inner-icon="mdi-magnify"
                        clearable
                        class="mb-6"
                    ></v-text-field>

                    <!-- Keywords by Category -->
                    <div class="keywords-container">
                        <div v-for="category in filteredKeywordCategories" :key="category.name" class="mb-6">
                            <h3 class="text-subtitle-1 font-weight-bold text-primary mb-3 d-flex align-center gap-2">
                                <v-icon size="small">{{ category.icon }}</v-icon>
                                {{ category.name }}
                            </h3>

                            <v-row>
                                <v-col v-for="item in category.keywords" :key="item.keyword" cols="12" md="6">
                                    <v-card
                                        variant="outlined"
                                        class="keyword-card h-100 hover-card"
                                        @click="copyKeyword(item.keyword)"
                                    >
                                        <v-card-text class="pa-4">
                                            <div class="d-flex justify-space-between align-start gap-2">
                                                <div class="flex-grow-1">
                                                    <div class="font-weight-bold text-primary mb-2 keyword-text">
                                                        {{ item.keyword }}
                                                    </div>
                                                    <div class="text-caption text-medium-emphasis">
                                                        {{ item.description }}
                                                    </div>
                                                </div>
                                                <v-btn
                                                    icon
                                                    size="x-small"
                                                    variant="text"
                                                    @click.stop="copyKeyword(item.keyword)"
                                                    title="Copy keyword"
                                                >
                                                    <v-icon size="small">mdi-content-copy</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </div>

                        <v-alert v-if="filteredKeywordCategories.length === 0" type="info" variant="tonal">
                            No keywords match your search. Try different keywords.
                        </v-alert>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-6">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        variant="flat"
                        @click="showKeywordsModal = false"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Copy Toast Notification -->
        <v-snackbar
            v-model="showCopyNotification"
            :timeout="2000"
            color="success"
        >
            <v-icon class="mr-2">mdi-check-circle</v-icon>
            Keyword copied to clipboard!
        </v-snackbar>
    </v-app>
</template>

<script>
import { Ckeditor } from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import { useRouter } from "vue-router";

import hrmLetterSetup from "@/components/module/hrm/settingsManagement/hrmLetterSetup.vue";

export default {
    components: {
        Ckeditor,
        hrmLetterSetup,
    },
    data: () => ({
        panel: [0],
        loading: false,
        dataLoaded: false,
        data: {},
        letterInfoDialog: false,
        selectedLetterId: null,
        showKeywordsModal: false,
        showCopyNotification: false,
        keywordSearch: "",
        keywords: [
            {
                category: "Personal Information",
                icon: "mdi-account",
                keywords: [
                    { keyword: "$EmployeeName$", description: "Full name of the employee" },
                    { keyword: "$EmployeeCode$", description: "Unique employee code/ID" },
                    { keyword: "$Email$", description: "Official email address" },
                    { keyword: "$Mobile$", description: "Mobile phone number" },
                    { keyword: "$FatherName$", description: "Father's name" },
                    { keyword: "$MotherName$", description: "Mother's name" },
                    { keyword: "$DateOfBirth$", description: "Date of birth" },
                    { keyword: "$Gender$", description: "Gender (Male/Female)" },
                ]
            },
            {
                category: "Contact Information",
                icon: "mdi-phone",
                keywords: [
                    { keyword: "$PhoneOffice$", description: "Office phone number" },
                    { keyword: "$PhoneResidence$", description: "Residential phone number" },
                    { keyword: "$EmergencyContactPerson$", description: "Name of emergency contact person" },
                    { keyword: "$EmergencyContactNo$", description: "Emergency contact phone number" },
                ]
            },
            {
                category: "Address Information",
                icon: "mdi-map-marker",
                keywords: [
                    { keyword: "$PermanentAddress$", description: "Permanent residential address" },
                ]
            },
            {
                category: "Organization Information",
                icon: "mdi-building",
                keywords: [
                    { keyword: "$CompanyName$", description: "Company/Branch name" },
                    { keyword: "$CompanyCode$", description: "Company/Branch code" },
                    { keyword: "$Department$", description: "Department name" },
                    { keyword: "$DepartmentCode$", description: "Department code" },
                    { keyword: "$Designation$", description: "Job designation/Title" },
                    { keyword: "$DesignationCode$", description: "Designation code" },
                ]
            },
            {
                category: "Employment Details",
                icon: "mdi-briefcase",
                keywords: [
                    { keyword: "$JoiningDate$", description: "Date of joining the organization" },
                    { keyword: "$EmployeeType$", description: "Type of employment (Full-time, Part-time, etc.)" },
                    { keyword: "$Status$", description: "Employment status (Active/Inactive)" },
                ]
            },
            {
                category: "Personal Details",
                icon: "mdi-information-outline",
                keywords: [
                    { keyword: "$BloodGroup$", description: "Blood group" },
                    { keyword: "$Religion$", description: "Religion" },
                    { keyword: "$Nationality$", description: "Nationality" },
                    { keyword: "$MaritalStatus$", description: "Marital status" },
                ]
            },
            {
                category: "Document Information",
                icon: "mdi-file-document",
                keywords: [
                    { keyword: "$NID$", description: "National ID number" },
                    { keyword: "$PassportNo$", description: "Passport number" },
                ]
            },
            {
                category: "Physical Information",
                icon: "mdi-human",
                keywords: [
                    { keyword: "$Height$", description: "Height" },
                    { keyword: "$Weight$", description: "Weight" },
                ]
            },
            {
                category: "User Account Information",
                icon: "mdi-account-circle",
                keywords: [
                    { keyword: "$Username$", description: "System username" },
                    { keyword: "$UserFullName$", description: "User account full name" },
                    { keyword: "$UserEmail$", description: "User account email address" },
                ]
            },
        ]
    }),
    created() {
        if (!this.$store.getters.getHrmLetterInfo?.data?.length) {
            this.$store.dispatch("getHrmLetterInfo");
        }
    },
    computed: {
        getHrmLetterInfo() {
            return this.$store.getters.getHrmLetterInfo;
        },
        filteredKeywordCategories() {
            if (!this.keywordSearch.trim()) {
                return this.keywords;
            }

            const searchLower = this.keywordSearch.toLowerCase();
            return this.keywords
                .map(category => ({
                    ...category,
                    keywords: category.keywords.filter(item =>
                        item.keyword.toLowerCase().includes(searchLower) ||
                        item.description.toLowerCase().includes(searchLower)
                    )
                }))
                .filter(category => category.keywords.length > 0);
        }
    },
    watch: {
        selectedLetterId(newId) {
            if (newId) {
                this.fetchLetterById(newId);
            } else {
                this.resetFormData();
            }
        }
    },
    methods: {
        setFormData(data) {
            console.log(data)
            this.subject.value.value = data.subject || "";
            this.to.value.value = data.to || "";
            this.body.value.value = data.body || "";
            this.terms.value.value = data.terms || "";
            this.regards.value.value = data.regards || "";
            this.id.value.value = data.id || "";
        },
        openAddLetterInfo() {
            this.data = {
                id: '',
                name: '',
                code: '',
                remarks: '',
                status: 1,
            };
            this.letterInfoDialog = true;
        },

        resetFormData() {
            this.subject.value.value = "";
            this.body.value.value = "";
            this.terms.value.value = "";
            this.regards.value.value = "";
            this.id.value.value = "";
        },

        async fetchLetterById(id) {
            try {
                const data = await this.$store.dispatch("getHrmLetterInfoById", id);
                console.log(data)
                this.setFormData(data);
            } catch (error) {
                console.log(error)
                toast.error(error.message || "Error fetching letter");
            }
        },

        copyKeyword(keyword) {
            navigator.clipboard.writeText(keyword).then(() => {
                this.showCopyNotification = true;
            }).catch(err => {
                console.error("Failed to copy:", err);
            });
        },

        submitForm() {
            this.loading = true;
            this.submit().finally(() => {
                this.loading = false;
            });
        },
    },
    setup() {
        const store = useStore();
        const router = useRouter();

        const { handleSubmit, resetForm } = useForm({
            validationSchema: yup.object({
                id: yup.string().required().label("Letter Type"),
                to: yup.string().nullable().label("To"),
                subject: yup.string().required().label("Subject"),
                terms: yup.string().nullable().label("Letter terms"),
                body: yup.string().nullable().label("Letter body"),
                regards: yup.string().nullable().label("Regards"),
            }),
        });

        const id = useField("id");
        const name = useField("name");
        const to = useField("to");
        const subject = useField("subject");
        const body = useField("body");
        const terms = useField("terms");
        const regards = useField("regards");

        const submit = handleSubmit((values) => {

            if (!values.to || !values.subject || !values.body || !values.regards) {
                toast.error("Please fill all the fields");
                return;
            } 

            const payload = {
                ...values,
            };

            store
                .dispatch("saveUpdateHrmLetterInfo", payload)
                .then((response) => {
                    toast.success(response.data.message, { timeout: 3000 });
                })
                .catch((error) => {
                    const message =
                        error.response?.data?.message || "Failed to update letter";
                    toast.error(message);
                    console.error("Submission error:", error);
                });
        });

        return {
            id,
            name,
            to,
            subject,
            body,
            terms,
            regards,
            submit,
            ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [ 
                        "heading", "|", "bold", "italic", "underline", "|", "link", "bulletedList", "numberedList", "|", "outdent", "indent", "|", "blockQuote", "|", "undo", "redo",
                    ],
                },
                language: "en",
            },
        };
    },
};
</script>

<style scoped>
.visually-hidden-label .v-field-label {
    display: none !important;
}

.panel-header {
    background-color: #CFD8DC !important;
    color:black !important;
    font-weight: bold;
    font-size: 1.1rem;
    padding-left: 20px !important;
}

.keywords-container {
    max-height: 600px;
    overflow-y: auto;
}

.keyword-card {
    transition: all 0.3s ease;
    cursor: pointer;
}

.keyword-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.keyword-text {
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    word-break: break-word;
}
</style>